// Network (plan step 3.4; D37, spec 8.4): requests grouped by endpoint template, one endpoint's
// statuses, days, releases and sample requests, and one trace: the device's spans and the backend
// spans the team's services sent over OTLP with the same trace id (D37, APS-507), in their own
// group. Every request opens the same trace in the team's APM when the project has a link
// (Settings › APM link).
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { type ReactNode, useState } from 'react'
import {
  endpointQuery,
  endpointsQuery,
  issuesQuery,
  sessionCountsQuery,
  sessionQuery,
  traceQuery,
  usageQuery,
} from '../api/queries'
import type { EndpointSummary, Endpoints, RequestSample, Trace } from '../api/types'
import {
  axisTicks,
  btnClass,
  copyText,
  EmptyNote,
  Hbar,
  HovRow,
  Kind,
  KV,
  LK,
  Pill,
  Section,
  Seg,
  ShareBars,
  SortTh,
  Spark,
  Strip,
  type StripItem,
  Table,
  useToast,
  Waterfall,
  type WaterfallItem,
  type WaterfallSpan,
} from '../components/d'
import { Icon } from '../components/icons'
import { LineChart } from '../components/LineChart'
import {
  ApproxFigure,
  Counted,
  Histogram,
  IssueTable,
  LatencyText,
  NoApp,
  PerfHead,
  perfMeta,
  ReleaseLink,
  releaseMarker,
  usePerfReleases,
  usePerfScope,
  withRelease,
} from '../components/Perf'
import { ErrorState, Loading } from '../components/states'
import { type Translator, useI18n } from '../i18n'
import { ATTR } from '../lib/conventions'
import { compact, duration, percentSpaced, shortId } from '../lib/format'
import { endpointAt, latency } from '../lib/latency'
import { parseTime, sessionModel } from '../lib/session'
import { splitMs } from './Performance'
import { useProjectParams } from './params'
import {
  carried,
  type ENDPOINT_SORTS,
  type EndpointSearch,
  type ListSearch,
  type PerfSearch,
} from './search'

/** A failure rate: one decimal from 1 %, two below it, so a rare failure still shows. */
const rate = (i18n: Translator, r: number | null | undefined) =>
  r === null || r === undefined ? '—' : percentSpaced(i18n, r, r >= 0.01 ? 1 : 2)

/** A rate as a strip figure and its unit: 0.37 %. */
const ratePart = (i18n: Translator, r: number | null | undefined): [string, string] =>
  r === null || r === undefined
    ? ['—', '']
    : [i18n.number(r * 100, { maximumFractionDigits: r >= 0.01 ? 1 : 2 }), '%']

type EndpointSort = (typeof ENDPOINT_SORTS)[number]

/** The section's sort, as the mock orders it: failures, volume, slowest. */
const SORTS = [
  ['error_rate', 'perf.network.sort.failures'],
  ['requests', 'perf.network.sort.volume'],
  ['p90', 'perf.network.sort.p90'],
] as const

/** Endpoints by host, the hosts in the order of their first endpoint in the sorted list. */
export function byHost(endpoints: EndpointSummary[]): { host: string; rows: EndpointSummary[] }[] {
  const groups = new Map<string, EndpointSummary[]>()
  for (const e of endpoints) {
    const rows = groups.get(e.host)
    if (rows) rows.push(e)
    else groups.set(e.host, [e])
  }
  return [...groups].map(([host, rows]) => ({ host, rows }))
}

/** The endpoint failing the most, if any fails. */
export function worstEndpoint(endpoints: EndpointSummary[]): EndpointSummary | undefined {
  let worst: EndpointSummary | undefined
  for (const e of endpoints) if ((e.error_rate ?? 0) > (worst?.error_rate ?? 0)) worst = e
  return worst
}

const LIMIT = 50

/** Sample rows a table shows, as the mock: the sessions link opens them all. */
const SAMPLES = 5

export function NetworkPage() {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as ListSearch
  const { scope, app, appsPending } = usePerfScope()
  const sort = (search.sort as EndpointSort | undefined) ?? 'requests'
  const endpoints = useQuery({
    ...endpointsQuery(projectId, scope, sort, LIMIT, undefined, true),
    enabled: !!scope.app_id,
  })
  const params = { orgId, projectId }
  const keep = { ...carried(search), app: scope.app_id }
  const title = t('perf.network.title')
  const page = t('perf.nav.network')

  if (appsPending)
    return (
      <main id="main" className="pg pf-pg">
        <Loading />
      </main>
    )
  if (!app)
    return (
      <main id="main" className="pg pf-pg">
        <PerfHead title={title} page={page} />
        <NoApp />
      </main>
    )

  const data = endpoints.data
  const list = data?.endpoints ?? []
  const groups = byHost(list)
  const worst = worstEndpoint(list)
  const maxFail = worst?.error_rate ?? 0
  const meta = perfMeta(i18n, app, scope, [
    data
      ? t('perf.network.metaCount', {
          endpoints: list.length,
          hosts: groups.length,
        })
      : null,
  ])
  const actions = (
    <>
      <Link
        className={btnClass()}
        to="/o/$orgId/p/$projectId/settings/$section"
        params={{ ...params, section: 'apm' }}
      >
        {t('perf.network.apmLink')}
      </Link>
      <Link
        className={btnClass()}
        to="/o/$orgId/p/$projectId/sessions"
        params={params}
        search={{ app: scope.app_id, failed_request: true }}
      >
        {t('perf.network.failedSessions')}
      </Link>
    </>
  )
  const toEndpoint = (e: EndpointSummary) => ({
    to: '/o/$orgId/p/$projectId/performance/network/endpoint' as const,
    params,
    search: { ...keep, app: e.app_id, method: e.method, host: e.host, template: e.template },
  })
  const setSort = (s: EndpointSort) =>
    navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        sort: s === 'requests' ? undefined : s,
      }),
    })
  const head = (s: EndpointSort, label: string) => (
    <SortTh on={sort === s} onSort={() => setSort(s)} className="num">
      {label}
    </SortTh>
  )

  const strip = (d: Endpoints): StripItem[] => {
    const [failV, failU] = ratePart(i18n, d.totals.error_rate)
    const [clientV, clientU] = ratePart(i18n, d.totals.client_error_rate)
    const p90 = endpointAt(d.totals, 'p90_ms')
    const [p90V, p90U] = splitMs(i18n, p90?.ms)
    return [
      {
        dt: t('perf.network.requests'),
        dd: compact(i18n, d.totals.requests),
        d:
          d.totals.exact_requests > 0 ? (
            <Counted exact={d.totals.exact_requests} estimated={d.totals.estimated_requests} />
          ) : (
            t('perf.network.weighted')
          ),
      },
      { dt: t('perf.network.failed'), dd: failV, small: failU, d: t('perf.network.failedNote') },
      {
        dt: t('perf.network.clientErrors'),
        dd: clientV,
        small: clientU,
        d: t('perf.network.clientNote'),
      },
      {
        dt: 'p90',
        dd: p90?.approx ? <ApproxFigure>{p90V}</ApproxFigure> : p90V,
        small: p90U,
        d: t('perf.network.p50All', { value: latency(i18n, endpointAt(d.totals, 'p50_ms')) }),
      },
    ]
  }

  // No request in scope: the page keeps its shape (APS-385), a dash in each cell and no unit
  // beside it, and the endpoints section says it has none.
  const empty = !data || list.length === 0
  const emptyStrip: StripItem[] = [
    { dt: t('perf.network.requests'), dd: '—' },
    { dt: t('perf.network.failed'), dd: '—' },
    { dt: t('perf.network.clientErrors'), dd: '—' },
    { dt: 'p90', dd: '—' },
  ]

  return (
    <main id="main" className="pg pf-pg">
      <PerfHead title={title} page={page} meta={meta} actions={actions} />
      {endpoints.isPending ? (
        <Loading />
      ) : endpoints.isError ? (
        <ErrorState error={endpoints.error} retry={() => endpoints.refetch()} />
      ) : (
        <>
          <Strip items={!data || empty ? emptyStrip : strip(data)} translate="no" />
          <BackendAllowance backendSpans={data?.backend_spans === true} />
          <Section
            title={t('perf.network.endpoints')}
            meta={t('perf.network.endpointsMeta')}
            right={
              empty ? undefined : (
                <Seg
                  label={t('perf.sortBy')}
                  options={SORTS.map(([value, key]) => ({ value, label: t(key) }))}
                  value={sort}
                  onChange={setSort}
                />
              )
            }
          >
            {empty ? (
              <EmptyNote title={t('perf.network.none')}>{t('perf.network.noneBody')}</EmptyNote>
            ) : (
              <Table label={t('perf.network.endpoints')} rowKeys className="pf-tbl nw-tb">
                <thead>
                  <tr>
                    <th scope="col">{t('perf.col.endpoint')}</th>
                    {head('requests', t('perf.network.requests'))}
                    {head('error_rate', t('perf.col.failed'))}
                    <th scope="col" className="num" translate="no">
                      p50
                    </th>
                    {head('p90', 'p90')}
                    <th scope="col" className="num">
                      {t('perf.network.col.trend')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => [
                    <tr key={`h ${g.host}`} className="grp">
                      <td colSpan={6} translate="no">
                        <span className="nw-host">{g.host}</span>
                        {t('perf.network.hostLine', {
                          count: g.rows.length,
                          requests: compact(
                            i18n,
                            g.rows.reduce((n, e) => n + e.requests, 0),
                          ),
                        })}
                      </td>
                    </tr>,
                    ...g.rows.map((e) => {
                      const bad = e === worst
                      return (
                        <HovRow
                          key={`${e.app_id} ${e.method} ${e.host} ${e.template}`}
                          cur={bad}
                          onOpen={() => navigate(toEndpoint(e))}
                        >
                          <td className="mono anywhere" translate="no">
                            <Link {...toEndpoint(e)}>
                              <span className="nw-m">{`${e.method} `}</span>
                              {e.template || '/'}
                            </Link>
                          </td>
                          <td className="num">{compact(i18n, e.requests)}</td>
                          <td className="num nw-fail">
                            <Hbar
                              share={maxFail > 0 ? (e.error_rate ?? 0) / maxFail : 0}
                              {...(bad ? { tone: 'bad' as const } : {})}
                            />
                            <span className={bad ? 'c-crash' : undefined}>
                              {rate(i18n, e.error_rate)}
                            </span>
                          </td>
                          <td className="num">
                            <LatencyText v={endpointAt(e, 'p50_ms')} />
                          </td>
                          <td className="num">
                            <LatencyText v={endpointAt(e, 'p90_ms')} />
                          </td>
                          <td className="num">
                            {e.daily ? <Spark values={e.daily.error_rate} /> : null}
                          </td>
                        </HovRow>
                      )
                    }),
                  ])}
                </tbody>
              </Table>
            )}
            {list.length === LIMIT ? (
              <p className="nw-note">{t('perf.network.capped', { n: LIMIT })}</p>
            ) : null}
            {list.some((e) => e.percentiles === 'approximate') ? (
              <p className="nw-note">{t('perf.approx.note')}</p>
            ) : null}
            <p className="nw-note">{t('perf.network.lede')}</p>
            <p className="nw-note">{t('perf.network.foot')}</p>
          </Section>
        </>
      )}
    </main>
  )
}

/** Sample requests: when, status, duration, the trace here, the session, device, release, APM. */
function SampleTable({
  label,
  rows,
  appId,
  spanless,
  none,
}: {
  label: string
  rows: RequestSample[]
  appId: string
  /** No span of the endpoint was released: say so rather than "none in this window". */
  spanless?: boolean
  /** What to say when there is no row, instead of "none in this window". */
  none?: string | undefined
}) {
  const i18n = useI18n()
  const { t, relative } = i18n
  const { orgId, projectId } = useProjectParams()
  const params = { orgId, projectId }
  if (rows.length === 0)
    return (
      <EmptyNote
        title={spanless ? t('perf.approx.spans') : (none ?? t('perf.endpoint.noSamples'))}
      />
    )
  return (
    <Table label={label} className="pf-tbl">
      <thead>
        <tr>
          <th scope="col">{t('perf.col.when')}</th>
          <th scope="col">{t('perf.col.status')}</th>
          <th scope="col" className="num">
            {t('perf.col.duration')}
          </th>
          <th scope="col">{t('perf.col.trace')}</th>
          <th scope="col">{t('sessions.col.session')}</th>
          <th scope="col">{t('sessions.col.device')}</th>
          <th scope="col">{t('releases.col.release')}</th>
          <th scope="col">{t('perf.col.apm')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((s) => (
          <tr key={s.span_id + s.at}>
            <td>{relative(s.at)}</td>
            <td translate="no">
              <SampleStatus s={s} />
            </td>
            <td className="num">{duration(i18n, s.duration_ms)}</td>
            <td className="mono">
              <Link
                to="/o/$orgId/p/$projectId/traces/$traceId"
                params={{ ...params, traceId: s.trace_id }}
                search={{ app: appId }}
                translate="no"
              >
                {shortId(s.trace_id)}
              </Link>
            </td>
            <td className="mono">
              <Link
                to="/o/$orgId/p/$projectId/sessions/$sessionId"
                params={{ ...params, sessionId: s.session_id }}
                search={{ app: appId }}
                translate="no"
              >
                {shortId(s.session_id)}
              </Link>
            </td>
            <td translate="no" title={s.os_version || undefined}>
              {s.device_model || '—'}
            </td>
            <td translate="no">{s.release ? <ReleaseLink release={s.release} /> : '—'}</td>
            <td>
              {s.apm_url ? (
                <a className={LK} href={s.apm_url} target="_blank" rel="noreferrer noopener">
                  {t('perf.apm.openShort')}
                </a>
              ) : (
                <span className="faint">—</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

/** A failed request (5xx, or no response) reads as a crash pill; the others as their code. */
const failedStatus = (status: number) => status === 0 || status >= 500

function SampleStatus({ s }: { s: Pick<RequestSample, 'status' | 'error_type'> }) {
  const text = s.status > 0 ? String(s.status) : s.error_type || '—'
  return failedStatus(s.status) && text !== '—' ? <Pill tone="crash">{text}</Pill> : text
}

export function EndpointPage() {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as EndpointSearch
  const { scope, app, appsPending } = usePerfScope()
  const { list, current, previous } = usePerfReleases(scope)
  const key =
    search.method && search.host && search.template
      ? { method: search.method, host: search.host, template: search.template }
      : undefined
  const appId = scope.app_id ?? ''
  const enabled = !!appId && !!key
  const detail = useQuery({
    ...endpointQuery(projectId, appId, key ?? { method: '', host: '', template: '' }, scope, true),
    enabled,
  })
  // Every release in the window, for "was … on the release before" (D103 #14).
  const all = useQuery({
    ...endpointQuery(
      projectId,
      appId,
      key ?? { method: '', host: '', template: '' },
      withRelease(scope, undefined),
    ),
    enabled: enabled && !!scope.release,
  })
  const issues = useQuery({
    ...issuesQuery(projectId, {
      app_id: appId,
      ...(scope.release ? { release: scope.release } : {}),
      endpoint: key,
      sort: 'sessions',
      days: scope.days,
      limit: 5,
      daily: true,
    }),
    enabled,
  })
  const affected = useQuery({
    ...sessionCountsQuery(projectId, {
      app_id: appId,
      ...(scope.release ? { release: scope.release } : {}),
      ...(key ? { endpoint: { ...key, failed: true } } : {}),
      days: Math.min(scope.days, 30),
    }),
    enabled,
  })
  const params = { orgId, projectId }
  const page = (
    <>
      <Link
        to="/o/$orgId/p/$projectId/performance/network"
        params={params}
        search={carried(search)}
      >
        {t('perf.nav.network')}
      </Link>
      {key ? (
        <>
          {' / '}
          <span translate="no">{key.host}</span>
        </>
      ) : null}
    </>
  )
  const title = (
    <span translate="no">
      {key ? `${key.method} ${key.template || '/'}` : t('perf.network.title')}
    </span>
  )

  if (!key)
    return (
      <main id="main" className="pg pf-pg">
        <PerfHead title={t('perf.network.title')} page={page} />
        <EmptyNote title={t('perf.endpoint.missing')} />
      </main>
    )
  if (appsPending || detail.isPending)
    return (
      <main id="main" className="pg pf-pg">
        <Loading />
      </main>
    )
  if (!app)
    return (
      <main id="main" className="pg pf-pg">
        <PerfHead title={title} page={page} mono />
        <NoApp />
      </main>
    )
  if (detail.isError)
    return (
      <main id="main" className="pg pf-pg">
        <PerfHead title={title} page={page} mono />
        <ErrorState error={detail.error} retry={() => detail.refetch()} />
      </main>
    )
  const d = detail.data
  const meta = (
    <>
      {perfMeta(i18n, app, scope, [key.host])}
      {' · '}
      {d.apm_link_configured ? (
        t('perf.endpoint.apmLinked')
      ) : (
        <>
          {t('perf.endpoint.apmMissing')}{' '}
          <Link
            className="ul"
            to="/o/$orgId/p/$projectId/settings/$section"
            params={{ ...params, section: 'apm' }}
          >
            {t('perf.apm.set')}
          </Link>
        </>
      )}
    </>
  )
  const sessionsLink = {
    to: '/o/$orgId/p/$projectId/sessions' as const,
    params,
    search: { app: d.app_id, ...key, failed: true },
  }
  const topIssue = issues.data?.issues[0]
  const actions = (
    <>
      <Link className={btnClass()} {...sessionsLink}>
        {t('perf.endpoint.failedSessions')}
      </Link>
      {topIssue ? (
        <Link
          className={btnClass()}
          to="/o/$orgId/p/$projectId/issues/$issueId"
          params={{ ...params, issueId: topIssue.id }}
          search={carried(search)}
          translate="no"
        >
          {t('perf.endpoint.issue', { id: topIssue.id.slice(0, 8) })}
        </Link>
      ) : null}
    </>
  )
  if (d.summary.requests === 0)
    return (
      <main id="main" className="pg pf-pg">
        <PerfHead title={title} page={page} meta={meta} actions={actions} mono />
        <EmptyNote title={t('perf.endpoint.none')} />
      </main>
    )

  const sum = d.summary
  const rels = (all.data ?? d).releases
  const was = rels.find((r) => r.release === previous)
  const failed = Math.round((sum.error_rate ?? 0) * sum.requests)
  const [failV, failU] = ratePart(i18n, sum.error_rate)
  const p90 = endpointAt(sum, 'p90_ms')
  const [p90V, p90U] = splitMs(i18n, p90?.ms)
  // None of its requests' spans was released: the histogram, the statuses and the samples, which
  // read spans only, have nothing; its percentiles are the rollups' (D114, APS-445).
  const spanless = sum.sampled === 0
  const strip: StripItem[] = [
    {
      dt: t('perf.network.requests'),
      dd: compact(i18n, sum.requests),
      d:
        sum.exact_requests > 0 ? (
          <Counted exact={sum.exact_requests} estimated={sum.estimated_requests} />
        ) : (
          t('perf.endpoint.sampled', { count: sum.sampled })
        ),
    },
    {
      dt: t('perf.network.failed'),
      dd: failV,
      small: failU,
      d: (
        <>
          <span className={failed > 0 ? 'c-crash' : undefined}>{i18n.number(failed)}</span>
          {previous && was ? (
            <>
              {' · '}
              {t('perf.endpoint.wasOn', { value: rate(i18n, was.error_rate), release: previous })}
            </>
          ) : null}
        </>
      ),
    },
    {
      dt: 'p90',
      dd: p90?.approx ? <ApproxFigure>{p90V}</ApproxFigure> : p90V,
      small: p90U,
      d: [
        t('perf.card.p50', { value: latency(i18n, endpointAt(sum, 'p50_ms')) }),
        previous && was
          ? t('perf.endpoint.wasOn', {
              value: latency(i18n, endpointAt(was, 'p90_ms')),
              release: previous,
            })
          : '',
      ]
        .filter(Boolean)
        .join(' · '),
    },
    ...(affected.data
      ? [
          {
            dt: t('perf.endpoint.affected'),
            dd: compact(i18n, affected.data.sessions),
            d: t('perf.endpoint.crashed', { count: affected.data.crashed }),
            link: ({ className, children }) => (
              <Link className={className} {...sessionsLink}>
                {children}
              </Link>
            ),
          } satisfies StripItem,
        ]
      : []),
  ]
  const marker = releaseMarker(
    list,
    current,
    d.days.map((x) => x.day),
  )
  const failedStatuses = d.statuses.filter((x) => failedStatus(x.status))
  const okTop = d.statuses
    .filter((x) => !failedStatus(x.status))
    .sort((a, b) => b.requests - a.requests)[0]
  const statusTotal = d.statuses.reduce((n, x) => n + x.requests, 0)

  return (
    <main id="main" className="pg pf-pg">
      <PerfHead title={title} page={page} meta={meta} actions={actions} mono />
      <Strip items={strip} translate="no" />
      <div className="grid2">
        <div className="pf-stack">
          <Section
            title={t('perf.endpoint.daily')}
            meta={t('perf.endpoint.dailyMeta', { days: scope.days })}
            right={
              marker ? (
                <Link
                  className={btnClass('ghost', 'sm')}
                  to="/o/$orgId/p/$projectId/releases/$release"
                  params={{ ...params, release: marker.label }}
                  search={{ ...carried(search), release: undefined }}
                  translate="no"
                >
                  {t('perf.endpoint.release', { release: marker.label })}
                </Link>
              ) : undefined
            }
          >
            <div className="pf-chart">
              {d.days.every((x) => x.error_rate === null) ? (
                <EmptyNote title={t('perf.trend.empty')} />
              ) : (
                <LineChart
                  label={t('perf.endpoint.dailyLabel', { days: scope.days })}
                  accent
                  area
                  marker={marker}
                  points={d.days.map((x) => ({
                    label: i18n.date(`${x.day}T00:00:00Z`, {
                      month: 'short',
                      day: 'numeric',
                      timeZone: 'UTC',
                    }),
                    value: x.error_rate,
                  }))}
                  format={(v) => percentSpaced(i18n, v, 1)}
                />
              )}
            </div>
          </Section>
          <Section
            title={t('perf.endpoint.issues')}
            meta={issues.data ? i18n.number(issues.data.issues.length) : undefined}
          >
            {issues.isPending ? (
              <Loading />
            ) : issues.isError ? (
              <ErrorState error={issues.error} retry={() => issues.refetch()} />
            ) : issues.data.issues.length === 0 ? (
              <EmptyNote title={t('perf.endpoint.noIssues')} />
            ) : (
              <IssueTable label={t('perf.endpoint.issues')} issues={issues.data.issues} />
            )}
            <p className="nw-cap">{t('perf.endpoint.issuesNote')}</p>
          </Section>
          <Section
            title={t('perf.endpoint.failed')}
            meta={t('perf.endpoint.ofCount', {
              shown: i18n.number(Math.min(d.failed.length, SAMPLES)),
              total: i18n.number(failed),
            })}
          >
            <SampleTable
              label={t('perf.endpoint.failed')}
              rows={d.failed.slice(0, SAMPLES)}
              appId={d.app_id}
              // Nothing failed: that, not the missing spans, is why there is no sample.
              spanless={spanless && failed > 0}
              none={failed === 0 ? t('perf.endpoint.noFailures') : undefined}
            />
          </Section>
          <Section
            title={t('perf.endpoint.slowest')}
            meta={t('perf.endpoint.ofCount', {
              shown: i18n.number(Math.min(d.slowest.length, SAMPLES)),
              total: i18n.number(sum.requests),
            })}
          >
            <SampleTable
              label={t('perf.endpoint.slowest')}
              rows={d.slowest.slice(0, SAMPLES)}
              appId={d.app_id}
              spanless={spanless}
            />
          </Section>
        </div>
        <div className="pf-stack">
          <Section
            title={t('perf.endpoint.statuses')}
            meta={t('perf.endpoint.statusesMeta', {
              failed: i18n.number(failed),
              total: i18n.number(sum.requests),
            })}
            className="nw-sh"
          >
            {spanless ? (
              <EmptyNote title={t('perf.approx.spans')} />
            ) : (
              <ShareBars
                label={t('perf.endpoint.statuses')}
                rows={[...d.statuses]
                  .sort((a, b) => b.requests - a.requests)
                  .map((x) => ({
                    key: `${x.status} ${x.error_type}`,
                    name: (
                      <span translate="no">{x.status > 0 ? x.status : x.error_type || '—'}</span>
                    ),
                    n: i18n.number(x.requests),
                    share: statusTotal > 0 ? x.requests / statusTotal : 0,
                    value: percentSpaced(i18n, statusTotal > 0 ? x.requests / statusTotal : 0, 0),
                    bad: failedStatus(x.status),
                  }))}
              />
            )}
            <p className="nw-cap">
              {[
                t('perf.endpoint.split', {
                  server: rate(i18n, sum.server_error_rate),
                  network: rate(i18n, sum.network_error_rate),
                }),
                failedStatuses.length === 0 && okTop
                  ? t('perf.endpoint.allOk', { status: okTop.status })
                  : null,
              ]
                .filter(Boolean)
                .join('. ')}
            </p>
          </Section>
          <Section
            title={t('perf.endpoint.histogram')}
            meta={
              spanless
                ? undefined
                : t('perf.endpoint.histogramMeta', {
                    count: sum.requests,
                    width: d.fine_histogram?.bucket_ms ?? 100,
                  })
            }
          >
            {spanless ? (
              <EmptyNote title={t('perf.approx.spans')}>{t('perf.approx.spansBody')}</EmptyNote>
            ) : (
              <Histogram
                data={d.histogram}
                fine={d.fine_histogram}
                p50={sum.p50_ms}
                p90={sum.p90_ms}
                height={170}
                label={t('perf.endpoint.histogramLabel', { count: sum.requests })}
              />
            )}
          </Section>
          <Section title={t('perf.endpoint.releases')}>
            {d.releases.length === 0 ? (
              <EmptyNote title={t('perf.card.none')} />
            ) : (
              <Table label={t('perf.endpoint.releases')} className="pf-tbl">
                <thead>
                  <tr>
                    <th scope="col">{t('releases.col.release')}</th>
                    <th scope="col" className="num">
                      {t('perf.endpoint.col.requests')}
                    </th>
                    <th scope="col" className="num">
                      {t('perf.col.failed')}
                    </th>
                    <th scope="col" className="num" translate="no">
                      p50
                    </th>
                    <th scope="col" className="num" translate="no">
                      p90
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rels.map((r) => (
                    <tr key={r.release}>
                      <td translate="no">
                        <ReleaseLink release={r.release} />
                      </td>
                      <td className="num">{compact(i18n, r.requests)}</td>
                      <td className="num">
                        <span
                          className={
                            r.release === current && (r.error_rate ?? 0) > (was?.error_rate ?? 0)
                              ? 'c-crash'
                              : undefined
                          }
                        >
                          {rate(i18n, r.error_rate)}
                        </span>
                      </td>
                      <td className="num">
                        <LatencyText v={endpointAt(r, 'p50_ms')} />
                      </td>
                      <td className="num">
                        <LatencyText v={endpointAt(r, 'p90_ms')} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>
        </div>
      </div>
    </main>
  )
}

type TraceSpan = Trace['spans'][number]

/** The trace's spans in tree order (each under its parent), with their depth. */
export function spanTree(spans: TraceSpan[]): { span: TraceSpan; depth: number }[] {
  const ids = new Set(spans.map((s) => s.span_id))
  const children = new Map<string, TraceSpan[]>()
  const roots: TraceSpan[] = []
  const byStart = (a: TraceSpan, b: TraceSpan) => parseTime(a.start) - parseTime(b.start)
  for (const s of [...spans].sort(byStart)) {
    if (s.parent_span_id && ids.has(s.parent_span_id) && s.parent_span_id !== s.span_id) {
      const list = children.get(s.parent_span_id)
      if (list) list.push(s)
      else children.set(s.parent_span_id, [s])
    } else roots.push(s)
  }
  const out: { span: TraceSpan; depth: number }[] = []
  const seen = new Set<string>()
  const walk = (s: TraceSpan, depth: number) => {
    if (seen.has(s.span_id)) return
    seen.add(s.span_id)
    out.push({ span: s, depth })
    for (const c of children.get(s.span_id) ?? []) walk(c, depth + 1)
  }
  for (const r of roots) walk(r, 0)
  return out
}

/**
 * Whether the team's backend spans can join its traces (APS-507, D110): they are data without a
 * session, which Free does not keep and Pro keeps up to its monthly allowance. Ingest refuses them
 * at the edge and records nothing, not even their trace, so this is said once here, never on each
 * trace (Mosaic), and only while the org's sessionless data is refused.
 */
function BackendAllowance({ backendSpans }: { backendSpans: boolean }) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const usage = useQuery(usageQuery(orgId))
  const refused = usage.data?.fair_use?.restrictions.sessionless_refused
  // A used-up allowance means the org did send data without a session this month. On Free, only
  // a project that already holds a backend's spans hears it: never a standing upsell (Mosaic).
  if (!refused || (refused === 'not_included' && !backendSpans)) return null
  return (
    <p className="nw-note nw-allowance">
      {t(refused === 'not_included' ? 'perf.network.backendFree' : 'perf.network.backendQuota')}
      {' · '}
      <Link
        className="ul"
        to="/o/$orgId/p/$projectId/settings/$section"
        params={{ orgId, projectId, section: 'plan' }}
      >
        {t(refused === 'not_included' ? 'perf.network.seePlans' : 'perf.network.seeUsage')}
      </Link>
    </p>
  )
}

/** After the trace, the same session's hangs and crashes within this many milliseconds. */
const AFTER_MS = 10_000

export function TracePage() {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as PerfSearch
  const { traceId } = useParams({ strict: false }) as { traceId?: string }
  const trace = useQuery({ ...traceQuery(projectId, traceId ?? ''), enabled: !!traceId })
  const spans = trace.data?.spans ?? []
  const sessionId = spans.find((s) => s.session_id)?.session_id ?? ''
  const session = useQuery({ ...sessionQuery(projectId, sessionId), enabled: !!sessionId })
  const [picked, setPicked] = useState<string | undefined>()
  const { toast, show } = useToast()
  const params = { orgId, projectId }
  const id = traceId ?? ''

  const tree = spanTree(spans)
  const start = spans.length ? Math.min(...spans.map((s) => parseTime(s.start))) : 0
  const end = spans.length ? Math.max(...spans.map((s) => parseTime(s.end))) : 0
  const root = tree.find((x) => x.span.http)?.span
  const appId = spans[0]?.app_id ?? (search.app as string | undefined)

  // the same session's hangs and crashes during the trace and just after it
  const model = session.data ? sessionModel(session.data) : undefined
  const after = model
    ? model.events.filter((e) => {
        const at = model.startedAt + e.at
        return (
          (e.signal === 'hang' || e.signal === 'crash' || e.signal === 'exit') &&
          at >= start &&
          at <= end + AFTER_MS
        )
      })
    : []
  const last = Math.max(
    end,
    ...(model ? after.map((e) => model.startedAt + e.at + e.duration) : []),
  )
  const ticks = axisTicks(last - start)
  const fmt = (v: number) => duration(i18n, v)

  // The device's spans, then the backend's (APS-507), each in the tree's order and depth.
  const deviceTree = tree.filter((x) => x.span.source !== 'backend')
  const backendTree = tree.filter((x) => x.span.source === 'backend')
  const items: WaterfallItem[] = []
  if (deviceTree.length > 0)
    items.push({
      key: 'g-device',
      group: t('perf.trace.device'),
      note: t('perf.trace.deviceNote', { id: shortId(id) }),
    })
  for (const { span: s, depth } of deviceTree)
    items.push({
      key: s.span_id,
      name: s.http ? s.http.method : s.name,
      sub: s.http ? `${s.http.template || '/'}` : s.attributes[ATTR.screenName],
      start: parseTime(s.start) - start,
      duration: s.duration_ms,
      depth,
      bar: s.http ? 'http' : undefined,
      // One treatment for the column (Mosaic): a failed status is crash text, never a pill.
      status: s.http ? (
        s.http.status > 0 && !failedStatus(s.http.status) ? (
          String(s.http.status)
        ) : (
          <span className="bad" translate="no">
            {s.http.status > 0 ? s.http.status : s.attributes['error.type'] || '—'}
          </span>
        )
      ) : (
        '—'
      ),
      value: fmt(s.duration_ms),
    })
  if (backendTree.length > 0)
    items.push({
      key: 'g-backend',
      backend: true,
      group: t('perf.trace.backend'),
      note: (
        <>
          {t('perf.trace.backendNote')}
          {trace.data?.apm_url ? (
            <>
              {' · '}
              <a className="ul" href={trace.data.apm_url} target="_blank" rel="noreferrer noopener">
                {t('perf.trace.backendApm')}
              </a>
            </>
          ) : null}
        </>
      ),
    })
  for (const { span: s, depth } of backendTree) {
    const code = s.attributes['http.response.status_code']
    const failed = s.status.code === 'ERROR'
    items.push({
      key: s.span_id,
      backend: true,
      name: s.service_name || s.name,
      sub: s.service_name ? s.name : undefined,
      start: parseTime(s.start) - start,
      duration: s.duration_ms,
      depth,
      // Status is text (Mosaic): a failed span's in the crash tone, without the pill. The code and
      // error.type are the service's own strings: never translated.
      status: failed ? (
        <span className="bad" translate="no">
          {code || s.attributes['error.type'] || t('perf.trace.backendError')}
        </span>
      ) : code ? (
        <span translate="no">{code}</span>
      ) : (
        '—'
      ),
      value: fmt(s.duration_ms),
    })
  }
  if (after.length > 0 && model) {
    items.push({ key: 'g-after', group: t('perf.trace.device'), note: t('perf.trace.afterNote') })
    for (const e of after)
      items.push({
        key: e.id,
        name: e.name,
        sub: e.sub,
        start: model.startedAt + e.at - start,
        duration: e.duration,
        bar: e.signal === 'hang' ? 'hang' : e.signal === 'crash' ? 'crash' : undefined,
        status:
          e.signal === 'crash' ? (
            <span className="c-crash">{t('perf.screen.crash')}</span>
          ) : e.signal === 'hang' ? (
            t('perf.trace.main')
          ) : (
            '—'
          ),
        value:
          e.signal === 'hang' ? (
            <Pill tone="warn">{fmt(e.duration)}</Pill>
          ) : (
            `+${fmt(model.startedAt + e.at - start)}`
          ),
      })
  }
  const spanItems = items.filter((x): x is WaterfallSpan => !('group' in x))
  const selected = picked ?? root?.span_id ?? spanItems[0]?.key
  const sel = spans.find((s) => s.span_id === selected)
  const selEvent = after.find((e) => e.id === selected)
  const summary = session.data?.summary
  const screen = spans.map((s) => s.attributes[ATTR.screenName]).find(Boolean)

  const attrs: [ReactNode, ReactNode][] = sel
    ? [
        ...(sel.source === 'backend'
          ? ([
              ['service.name', sel.service_name],
              ['span.kind', sel.kind || '—'],
              [
                'status',
                sel.status.message ? `${sel.status.code} · ${sel.status.message}` : sel.status.code,
              ],
              [t('perf.trace.source'), t('perf.trace.sourceBackend')],
            ] as [string, string][])
          : []),
        ...Object.entries(sel.attributes).sort(([a], [b]) => a.localeCompare(b)),
        [t('perf.trace.durationKey'), fmt(sel.duration_ms)],
        ...(model
          ? ([[t('perf.trace.sessionTime'), fmt(parseTime(sel.start) - model.startedAt)]] as [
              string,
              string,
            ][])
          : []),
      ]
    : selEvent && model
      ? [
          ...Object.entries(selEvent.attributes).sort(([a], [b]) => a.localeCompare(b)),
          ...(selEvent.duration > 0
            ? ([[t('perf.trace.durationKey'), fmt(selEvent.duration)]] as [string, string][])
            : []),
          [t('perf.trace.sessionTime'), fmt(selEvent.at)],
        ]
      : []

  const title = (
    <span translate="no" title={id}>
      {shortId(id)}
    </span>
  )
  const page = root?.http ? (
    <>
      <Link
        to="/o/$orgId/p/$projectId/performance/network"
        params={params}
        search={{ ...carried(search), app: appId }}
      >
        {t('perf.nav.network')}
      </Link>
      {' / '}
      <Link
        to="/o/$orgId/p/$projectId/performance/network/endpoint"
        params={params}
        search={{
          ...carried(search),
          app: root.app_id,
          method: root.http.method,
          host: root.http.host,
          template: root.http.template,
        }}
        translate="no"
      >
        {`${root.http.method} ${root.http.template || '/'}`}
      </Link>
    </>
  ) : (
    t('perf.trace.kicker')
  )
  const meta = summary ? (
    <>
      {t('perf.trace.session')}{' '}
      <Link
        className="ul"
        to="/o/$orgId/p/$projectId/sessions/$sessionId"
        params={{ ...params, sessionId }}
        search={{ app: appId }}
      >
        {shortId(sessionId)}
      </Link>
      {[summary.device_model, [summary.os_name, summary.os_version].filter(Boolean).join(' ')]
        .filter(Boolean)
        .map((x) => ` · ${x}`)
        .join('')}
      {summary.release ? (
        <>
          {' · '}
          <ReleaseLink release={summary.release} />
        </>
      ) : null}
      {spans.length ? ` · ${i18n.relative(new Date(start).toISOString())}` : ''}
    </>
  ) : undefined
  const actions = (
    <>
      <button
        type="button"
        className={btnClass()}
        onClick={async () => {
          if (await copyText(id)) show(t('perf.trace.copied'))
        }}
      >
        <Icon name="copy" />
        {t('perf.trace.copy')}
      </button>
      {trace.data?.apm_url ? (
        <a
          className={btnClass('solid')}
          href={trace.data.apm_url}
          target="_blank"
          rel="noreferrer noopener"
        >
          {t('perf.apm.open')}
        </a>
      ) : null}
    </>
  )

  return (
    <main id="main" className="pg pf-pg">
      <PerfHead title={title} page={page} meta={meta} actions={actions} mono />
      {/* What joins the two sides: only a trace that has both (Tide). */}
      {deviceTree.length > 0 && backendTree.length > 0 ? (
        <p className="tr-lede">{t('perf.trace.lede')}</p>
      ) : null}
      {toast}
      {trace.isPending ? (
        <Loading />
      ) : trace.isError ? (
        <ErrorState error={trace.error} retry={() => trace.refetch()} />
      ) : (
        <div className="grid2">
          <Section
            title={t('perf.trace.waterfall')}
            meta={
              deviceTree.length > 0 && backendTree.length > 0
                ? t('perf.trace.waterfallMetaMixed', {
                    count: deviceTree.length,
                    backend: backendTree.length,
                    duration: fmt(end - start),
                  })
                : deviceTree.length > 0
                  ? t('perf.trace.waterfallMeta', {
                      count: deviceTree.length,
                      duration: fmt(end - start),
                    })
                  : t('perf.trace.waterfallMetaBackend', {
                      count: backendTree.length,
                      duration: fmt(end - start),
                    })
            }
          >
            <Waterfall
              variant="trace"
              i18n={i18n}
              label={t('perf.trace.waterfallLabel', { id: shortId(id) })}
              items={items}
              ticks={ticks}
              heads={{
                span: t('perf.trace.span'),
                status: t('perf.col.status'),
                duration: t('perf.col.duration'),
              }}
              selected={selected}
              onSelect={setPicked}
            />
            {trace.data.truncated ? (
              <p className="nw-note">{t('perf.trace.truncated', { count: spans.length })}</p>
            ) : null}
            {/* traceparent joins a device request to the backend; a lone backend trace says why. */}
            <p className="nw-note">
              {deviceTree.length === 0
                ? t('perf.trace.backendOnly')
                : trace.data.apm_url
                  ? t('perf.trace.footApm')
                  : t('perf.trace.foot')}
            </p>
          </Section>
          <div className="pf-stack">
            <Section title={t('perf.trace.span')} meta={t('perf.trace.attributes')}>
              {sel || selEvent ? (
                <>
                  <p className="nw-kv-h" translate="no">
                    {sel
                      ? sel.http
                        ? `${sel.http.method} ${sel.http.host}${sel.http.template || '/'}`
                        : sel.name
                      : `${selEvent?.name} ${selEvent?.sub ?? ''}`}
                  </p>
                  <KV
                    className="nw-kv"
                    label={t('perf.trace.attributes')}
                    items={attrs.map(([k, v]) => [
                      <span key="k" translate="no">
                        {k}
                      </span>,
                      <span key="v" translate="no">
                        {v}
                      </span>,
                    ])}
                  />
                </>
              ) : (
                <EmptyNote title={t('perf.trace.noSpan')} />
              )}
            </Section>
            {/* A trace only the backend sent has no session, endpoint or screen to link to. */}
            {sessionId || root?.http || screen ? (
              <Section title={t('perf.trace.links')}>
                <ul className="rl pf-rl tr-links">
                  {sessionId ? (
                    <li>
                      <Link
                        className="ul"
                        to="/o/$orgId/p/$projectId/sessions/$sessionId"
                        params={{ ...params, sessionId }}
                        search={{ app: appId }}
                        translate="no"
                      >
                        {t('perf.trace.sessionLink', { id: shortId(sessionId) })}
                      </Link>
                      <span>{summary?.outcome ?? ''}</span>
                    </li>
                  ) : null}
                  {(session.data?.issues ?? []).map((x) => (
                    <li key={x.id}>
                      <Link
                        className="ul"
                        to="/o/$orgId/p/$projectId/issues/$issueId"
                        params={{ ...params, issueId: x.id }}
                        search={carried(search)}
                        translate="no"
                      >
                        {`${x.id.slice(0, 8)} · ${x.title || x.type}`}
                      </Link>
                      <span>
                        <Kind k={x.kind} />
                      </span>
                    </li>
                  ))}
                  {root?.http ? (
                    <li>
                      <Link
                        className="ul"
                        to="/o/$orgId/p/$projectId/performance/network/endpoint"
                        params={params}
                        search={{
                          ...carried(search),
                          app: root.app_id,
                          method: root.http.method,
                          host: root.http.host,
                          template: root.http.template,
                        }}
                        translate="no"
                      >
                        {`${root.http.method} ${root.http.template || '/'}`}
                      </Link>
                      <span>{t('perf.trace.endpoint')}</span>
                    </li>
                  ) : null}
                  {screen ? (
                    <li>
                      <Link
                        className="ul"
                        to="/o/$orgId/p/$projectId/performance/screens/detail"
                        params={params}
                        search={{ ...carried(search), app: appId, screen }}
                        translate="no"
                      >
                        {screen}
                      </Link>
                      <span>{t('perf.trace.screen')}</span>
                    </li>
                  ) : null}
                  {summary?.release ? (
                    <li>
                      <ReleaseLink release={summary.release} />
                      <span>{t('perf.trace.release')}</span>
                    </li>
                  ) : null}
                </ul>
              </Section>
            ) : null}
          </div>
        </div>
      )}
    </main>
  )
}
